import { isStandardRole, useUserRoleForCurrentWorkspace } from '@/features/roles';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Role } from '@customerio/hydra-permissions/roles';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Image,
  Paragraph,
  PlainButton,
  SegmentedControl,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useSampleWorkspace } from '@/features/workspaces';
import { useSetupGuideExperiment } from '@/features/experiments';
import { useThemeContext } from '@/context/theme';
import heartPride from '@/assets/images/heart-pride.svg';
import * as styles from './index.css';
import { BRAND_EVERGREEN, BRAND_VERDANT, fireConfetti } from '@/components/feedback/confetti';
import confetti from 'canvas-confetti';
import { HealthLabel } from '@/features/workspace-health';
import { sessionsStore } from '@customerio/hydra-session-state';
import { useAnalytics } from '@/services/analytics';

function currentUserRoleString(role: string) {
  const roleStrings: Record<Role, string> = {
    [Role.Admin]: 'an Account admin',
    [Role.Manager]: 'a Workspace admin',
    [Role.Author]: 'an Author',
    [Role.AuthorRedacted]: 'an Author',
    [Role.AuthorRedactedSensitive]: 'an Author',
    [Role.AuthorRedactedAll]: 'an Author',
    [Role.Viewer]: 'a Viewer',
    [Role.ViewerRedacted]: 'a Viewer',
    [Role.ViewerRedactedSensitive]: 'a Viewer',
    [Role.ViewerRedactedAll]: 'a Viewer',
  };

  if (isStandardRole(role)) {
    return `You're ${roleStrings[role]}`;
  }

  return role;
}

const heartShape = confetti.shapeFromPath({
  path: 'M14.7,2.3c-1.6-1.6-4.3-1.6-6,0l-.8.8-.8-.8C5.6.6,2.9.6,1.3,2.3s-1.6,4.3,0,6l.8.8h0s6,6,6,6h0s0,0,0,0l6-6h0s.8-.8.8-.8c1.6-1.6,1.6-4.3,0-6Z',
});

export function PersonDropdown() {
  const router = useRouter();
  const auth = useStore(authStore);
  const ref = useRef(null);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();
  const sessions = useStore(sessionsStore);
  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  const isSetupGuideIncomplete = !isSetupGuideLoading && setupGuide.completed_at == null;

  const showSetUpWorkspaceNavItem =
    isSetupGuideIncomplete &&
    window.sessionStorage.getItem('showSetUpWorkspaceNavItem') === 'true' &&
    !isSampleWorkspace(currentWorkspace?.id);

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);
  const isShowingMasqBarRef = useRef(isShowingMasqBar);
  isShowingMasqBarRef.current = isShowingMasqBar;
  const { track } = useAnalytics();

  const onMasqueradeChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      event.preventDefault();

      const newValue = !isShowingMasqBarRef.current;
      setMasqBarVisibility(newValue);
      setIsShowingMasqBar(newValue);

      // If turning off masq bar, remove masq_account_id from URL
      if (!newValue) {
        const url = new URL(window.location.href);
        url.searchParams.delete('masq_account_id');
        window.location.href = url.toString();
      } else {
        // Reload the page, so that the Journeys UI picks up the local storage update
        window.location.reload();
      }
    },
    [setIsShowingMasqBar, setMasqBarVisibility],
  );

  const onHeartClick = () => {
    fireConfetti(ref.current, {
      // These hex values match the colors from the heart svg, with the addition of our brand colors
      colors: [
        BRAND_EVERGREEN,
        BRAND_VERDANT,
        '#ea3041',
        '#f77e44',
        '#fbd733',
        '#5fbb5a',
        '#2799dd',
        '#1566bf',
        '#6d28ae',
      ],
      shapes: [heartShape],
    });
  };

  const { colorScheme, setColorScheme } = useThemeContext();

  const onColorSchemeChange = useCallback(
    (value: string) => {
      const nextColorScheme = value as ColorSchemePreference;

      if (nextColorScheme === colorScheme) return;

      setColorScheme(nextColorScheme);
      void track('darkMode', 'colorSchemeChanged', {
        'Color Scheme': nextColorScheme,
        'Previous Color Scheme': colorScheme,
      });
    },
    [colorScheme, setColorScheme, track],
  );

  return (
    <DropdownMenu initialFocus={-1}>
      {(context) => (
        <IconButton
          label="Menu"
          icon="manage"
          size="md"
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        />
      )}
      <DropdownMenuContent>
        {userRole != null || showSetUpWorkspaceNavItem ? (
          <DropdownMenuGroup>
            {userRole != null ? (
              <DropdownMenuItem>
                {currentUserRoleString(userRole.name)}
                {userRole.redacted ? (
                  <Box style={{ width: '20px', height: '20px' }} ml="100">
                    <Icon name="view-off" size="fill" display="block" />
                  </Box>
                ) : null}
              </DropdownMenuItem>
            ) : null}
            {showSetUpWorkspaceNavItem ? (
              <DropdownMenuItem
                href={
                  router.buildLocation({
                    to: '/workspaces/$workspaceId/home',
                    params: { workspaceId: currentWorkspace?.id },
                  }).href
                }
              >
                Set up workspace
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
        ) : null}

        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings/personal">Personal settings</DropdownMenuItem>
          <DropdownMenuItem href="/settings/experimental-features">Experimental features</DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
          {auth.account?.partner_account === true && hasPermission('account/edit') === true ? (
            <DropdownMenuItem href="/settings/partner">Partner settings</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/add">Invite teammate</DropdownMenuItem>
          ) : null}
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/invite-guest">Invite guest</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem href="/settings/privacy">Privacy &amp; Data</DropdownMenuItem>
          {hasPermission('account/support_access/view') === true ? (
            <DropdownMenuItem href="/settings/ai">AI &amp; MCP</DropdownMenuItem>
          ) : null}
          {hasPermission('account/billing/view') === true ? (
            <DropdownMenuItem href="/settings/billing/your-plan">Plans &amp; Billing</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/settings',
                params: { workspaceId: currentWorkspace?.id ?? 'last' },
              }).href
            }
          >
            Workspace settings
          </DropdownMenuItem>
          <DropdownMenuItem
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/health',
                params: { workspaceId: currentWorkspace?.id },
              }).href
            }
          >
            <Box display="flex" alignItems="center" gap="200" justifyContent="space-between">
              <Text>Workspace performance</Text> <HealthLabel />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuItem>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <SegmentedControl
                flexGrow="1"
                size="sm"
                label="Theme"
                value={colorScheme}
                onChange={onColorSchemeChange}
                options={[
                  { value: 'auto', label: 'System' },
                  { value: 'light', label: 'Light', icon: 'light-mode' },
                  { value: 'dark', label: 'Dark', icon: 'dark-mode' },
                ]}
              />
            </Box>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {showAdminMasqueradeOption ? (
          <DropdownMenuGroup>
            {/* oxlint-disable pluma/enforce-pluma-elements -- menu item acts as the label surface for the toggle. */}
            <DropdownMenuItem
              as="label"
              id="toggle-admin-masq-label"
              htmlFor="toggle-admin-masq"
              shouldCloseOnClick={false}
              // noop onClick to make the whole item styled as interactive
              onClick={() => {}}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text>Admin masquerade</Text>
                <Toggle
                  size="sm"
                  onChange={(event) => onMasqueradeChange(event)}
                  ariaLabelledby="toggle-admin-masq-label"
                  id="toggle-admin-masq"
                  isChecked={isShowingMasqBar}
                />
              </Box>
            </DropdownMenuItem>
            {/* oxlint-enable pluma/enforce-pluma-elements */}
          </DropdownMenuGroup>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem href="/logout" isDanger={true}>
            {sessions.length > 1 ? 'Log out from all accounts' : 'Log out'}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuFooter className={styles.dropdownFooter}>
          <Paragraph sizeVariant="xs" display="flex" flexWrap="wrap" alignItems="center" gap="050">
            <span id="made-with">Made with</span>
            <PlainButton
              aria-labelledby="made-with heart-love around-the-world"
              ref={ref}
              className={styles.heartContainer}
              onClick={onHeartClick}
            >
              <Icon className={styles.heartContainerHeartPlain} name="heart" id="heart-love" aria-label="love" />
              <Image className={styles.heartContainerHeartPride} src={heartPride} aria-label="love" />
            </PlainButton>
            <span id="around-the-world">around the world.</span>
          </Paragraph>
        </DropdownMenuFooter>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
